import { isListingStatus, LISTING_STATUS_LABELS, type ListingStatus } from "@workspace/listing-requirements";

export type ListingStatusKey =
  | "draft"
  | "pending"
  | "live"
  | "offline"
  | "rejected"
  | "deleted"
  | "unknown";

export interface ListingStatusInput {
  listingStatus?: string | null;
  isActive?: boolean | null;
  /**
   * @deprecated Owner-paused state is now expressed exclusively through
   * `listingStatus === "inactive"`. This field is accepted for backwards
   * compatibility with old payloads but no longer participates in display
   * logic — pass it or omit it and the result is identical.
   */
  disabledByOwner?: boolean | null;
}

export interface ListingStatusDisplay {
  key: ListingStatusKey;
  label: string;
  isLive: boolean;
}

const DISPLAY_BY_STATUS: Record<ListingStatus, Exclude<ListingStatusKey, "unknown">> = {
  incomplete: "draft",
  ready_to_submit: "draft",
  awaiting_approval: "pending",
  // `listing_status === "active"` is the single source of truth for
  // "live to renters" (task #2076). The legacy `cars.is_active` column
  // was dropped by task #1668 and `isActive` is now derived from
  // listing_status server-side, but the wizard's edit-detail endpoint
  // still returns the raw cars row where the column is missing — that
  // made the host's Status tab render "Paused" on a fully-live car
  // because the previous `listingStatus==="active" && !!isActive` rule
  // collapsed to false. Treat "active" as "live" unconditionally.
  active: "live",
  inactive: "offline",
  rejected: "rejected",
  deleted: "deleted",
};

const LABEL_BY_KEY: Record<ListingStatusKey, string> = {
  draft: "Draft",
  pending: "Pending review",
  live: "Live",
  offline: "Paused",
  rejected: "Rejected",
  deleted: "Removed",
  unknown: "Unknown",
};

/**
 * Maps the canonical 7-value `listing_status` (incomplete, ready_to_submit,
 * awaiting_approval, active, inactive, rejected, deleted) to the smaller set
 * of UI display states used by host-facing surfaces. Both `incomplete` and
 * `ready_to_submit` collapse to the "draft" display state — the readiness
 * checklist surfaces the difference between them. Any value that isn't a
 * canonical status (including null/empty) falls through to "unknown" so that
 * unexpected data is visibly surfaced rather than silently shown as a draft.
 */
export function getListingStatusDisplay({
  listingStatus,
}: ListingStatusInput): ListingStatusDisplay {
  if (!isListingStatus(listingStatus)) {
    return { key: "unknown", label: LABEL_BY_KEY.unknown, isLive: false };
  }
  const key = DISPLAY_BY_STATUS[listingStatus];
  return { key, label: LABEL_BY_KEY[key], isLive: key === "live" };
}

export function isListingLive(input: ListingStatusInput): boolean {
  return getListingStatusDisplay(input).isLive;
}

/** A recorded lifecycle is history, never authority over current availability. */
export function getEffectiveListingLifecycle(
  listingStatus: string | null | undefined,
  recordedState: string | null | undefined,
): "listed" | "snoozed" | "delisted" | "archived" | null {
  if (listingStatus === "active") return "listed";
  if (listingStatus === "inactive") return recordedState === "delisted" ? "delisted" : "snoozed";
  if (listingStatus === "deleted") return "archived";
  return null;
}

/** Admins need to distinguish unfinished drafts from drafts ready for submission. */
export function getAdminListingStatusDisplay(input: ListingStatusInput): ListingStatusDisplay {
  const display = getListingStatusDisplay(input);
  return { ...display, label: isListingStatus(input.listingStatus) ? LISTING_STATUS_LABELS[input.listingStatus] : display.label };
}

/**
 * Status-priority weight for the host's "My Garage" default ordering.
 *
 * Lower weights sort first. The intent is to surface listings that need the
 * host's attention before everything else:
 *
 *   0  needs action — drafts (incomplete/ready_to_submit) and rejected
 *   1  paused       — owner-paused (listing_status === "inactive" -> "offline")
 *   2  live         — active and visible to renters
 *   3  pending      — awaiting moderation (no host action available)
 *   4  other        — unknown / deleted (deleted is filtered out of the garage)
 *
 * Callers pair this with a newest-first tiebreaker so the order within each
 * status group still reads chronologically.
 */
export function getListingSortWeight(input: ListingStatusInput): number {
  const { key } = getListingStatusDisplay(input);
  switch (key) {
    case "draft":
    case "rejected":
      return 0;
    case "offline":
      return 1;
    case "live":
      return 2;
    case "pending":
      return 3;
    default:
      return 4;
  }
}
